/**
 * API · core.attachments + bucket storage 'core-attachments'.
 *
 * Convención de path: '<owner_kind>/<owner_id>/<uuid>-<filename>'
 * RLS de storage.objects + RLS de core.attachments enforcement por server.
 */
import { supabase } from '@/lib/supabase';
import type { AttachmentOwner, AttachmentsRow } from '@/types/database.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const BUCKET = 'core-attachments';
const MAX_SIZE = 20 * 1024 * 1024;

export interface UploadAttachmentParams {
  file: File;
  ownerKind: AttachmentOwner;
  ownerId: string;
  uploadedBy: string;
}

export async function uploadAttachment({
  file,
  ownerKind,
  ownerId,
  uploadedBy,
}: UploadAttachmentParams): Promise<AttachmentsRow> {
  if (file.size > MAX_SIZE) {
    throw new Error(`Archivo demasiado grande (máx 20 MB)`);
  }
  // Path único: kind/id/<uuid>-<filename safe>
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const uuid = crypto.randomUUID();
  const storagePath = `${ownerKind}/${ownerId}/${uuid}-${safeName}`;

  // 1. Upload al bucket
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: false,
    });
  if (upErr) throw upErr;

  // 2. Registrar puntero en core.attachments
  const { data, error: insErr } = await db
    .from('attachments')
    .insert({
      owner_kind: ownerKind,
      owner_id: ownerId,
      storage_path: storagePath,
      filename: file.name,
      mime_type: file.type || null,
      size_bytes: file.size,
      uploaded_by: uploadedBy,
    })
    .select('*')
    .single();
  if (insErr) {
    // Cleanup del object si la inserción falla
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw insErr;
  }
  return data as AttachmentsRow;
}

export async function listAttachmentsForOwners(
  ownerKind: AttachmentOwner,
  ownerIds: string[],
): Promise<AttachmentsRow[]> {
  if (ownerIds.length === 0) return [];
  const { data, error } = await db
    .from('attachments')
    .select('*')
    .eq('owner_kind', ownerKind)
    .in('owner_id', ownerIds)
    .is('deleted_at', null);
  if (error) throw error;
  return (data ?? []) as AttachmentsRow[];
}

export async function getSignedUrl(storagePath: string, expiresIn = 3600): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, expiresIn);
  if (error) throw error;
  return data.signedUrl;
}

export async function softDeleteAttachment(id: string): Promise<void> {
  const { error } = await db
    .from('attachments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
